// Game settings → Webhooks (spec/dashboard.md section 4.6 and 12.4; spec/api-conventions.md section 21.5): the game's
// webhooks (`manage_settings` reads and writes them). Rows `webhook-<id>`; Create… (`create-webhook-dialog`) and Rotate
// secret hand the signing secret to `SecretOnce`, the one place it is ever shown; Edit… (`edit-webhook-dialog`) patches
// url / events / description with If-Match; the Enabled switch patches `enabled`; Ping queues a `ping` delivery; Delete
// confirms; Deliveries… opens the drawer (`?deliveries=<hook>`) with the status filter and Redeliver.
import { useEffect, useState, type FormEvent, type KeyboardEvent } from 'react';
import { ops } from '../../api/operations';
import type { Webhook, WebhookCreated, WebhookDelivery } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { useGame } from '../../shell/GameContext';
import { Button } from '../../ui/Button';
import { Badge, Chip, type Tone } from '../../ui/Chip';
import { useConfirm } from '../../ui/ConfirmDialog';
import { Field } from '../../ui/Field';
import { Checkbox, Select, Switch, TextArea, TextInput } from '../../ui/inputs';
import { Modal, useUrlParam } from '../../ui/Modal';
import { formatInstantWithZone, useTimeZone } from '../../ui/time';
import { useToasts } from '../../ui/Toast';
import { queryCache, useQuery } from '../../workspace/cache';
import { asApiError } from './keys';
import { SecretOnce } from './SecretOnce';
import '../org/org.css';
import { LoadError, SkeletonRows } from '../../ui/PageStates';
import { EmptyState } from '../../ui/Banner';
import { Inbox, Lock, Plus, RefreshCw, Webhook as WebhookIcon } from '../../ui/icons';

/** The kind patterns offered as hints (an exact kind, a family with `.*`, or `*`; section 21.5). */
export const EVENT_HINTS = ['branch.head', 'release_pin.create', 'release.*', 'merge_request.*', 'job.*', 'job.finish', 'order.*', 'player.*', 'webhook.failed', '*'];

const DELIVERY_TONE: Record<string, Tone> = { pending: 'info', delivered: 'success', failed: 'danger' };

export function WebhooksTab() {
  const { org, game, can, missing, offsets } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const confirm = useConfirm();
  const [zone] = useTimeZone();
  const when = (v: string | null | undefined) => (v ? formatInstantWithZone(v, zone, { offsets }) : '—');
  const allowed = can('manage_settings');
  const key = `game:${org}/${game.slug}:webhooks`;
  const hooksQ = useQuery(allowed ? key : null, () => client.data(ops.listWebhooks, { path: { org, game: game.slug } }));
  const reload = () => queryCache.invalidate(key);
  const hookPath = (h: Webhook) => ({ org, game: game.slug, hook: h.id });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Webhook | null>(null);
  const [secret, setSecret] = useState<{ title: string; secret: string; hint: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const deliveries = useUrlParam('deliveries');

  const act = async (id: string, what: string, fn: () => Promise<void>) => {
    setBusy(id);
    try {
      await fn();
    } catch (e) {
      const err = asApiError(e);
      toasts.error(`${what}: ${err.message}`, { requestId: err.requestId });
    } finally {
      setBusy(null);
    }
  };

  const setEnabled = (h: Webhook, enabled: boolean) =>
    act(`${h.id}:enabled`, enabled ? 'Enable failed' : 'Disable failed', async () => {
      await client.call(ops.updateWebhook, { path: hookPath(h), body: { enabled }, ifMatch: Number(h.version) });
      toasts.success(enabled ? 'Webhook enabled: deliveries resume from the stream’s current position.' : 'Webhook disabled: no new deliveries are queued.');
      reload();
    });
  const rotate = async (h: Webhook) => {
    if (!(await confirm({ title: 'Rotate the signing secret?', body: 'Deliveries sent from now on are signed with a new secret; update the receiver at once.', confirmLabel: 'Rotate secret', tone: 'danger' }))) return;
    await act(`${h.id}:rotate`, 'Rotate failed', async () => {
      const r = await client.data(ops.rotateWebhookSecret, { path: hookPath(h), ifMatch: Number(h.version) });
      setSecret({ title: 'Signing secret rotated', secret: r.secret, hint: r.webhook.secretHint });
      reload();
    });
  };
  const ping = (h: Webhook) =>
    act(`${h.id}:ping`, 'Ping failed', async () => {
      await client.data(ops.pingWebhook, { path: hookPath(h) });
      toasts.success('Ping queued: the deliveries list shows the receiver’s answer within seconds.');
    });
  const remove = async (h: Webhook) => {
    if (!(await confirm({ title: 'Delete this webhook?', body: `${h.url} stops receiving events; its pending deliveries are never sent.`, confirmLabel: 'Delete webhook', tone: 'danger' }))) return;
    await act(`${h.id}:delete`, 'Delete failed', async () => {
      await client.call(ops.deleteWebhook, { path: hookPath(h), ifMatch: Number(h.version) });
      toasts.success('Webhook deleted.');
      reload();
    });
  };

  if (!allowed) {
    return (
      <div data-testid="settings-webhooks">
        <div className="pane">
          <div className="pane-head">
            <h2>Webhooks</h2>
            <div className="spacer" />
            <Button variant="primary" icon={<Plus aria-hidden="true" />} missingPermission={missing('manage_settings')}>
              Create…
            </Button>
          </div>
          <div className="pane-body">
            <p className="muted settings-locked" role="note">
              <Lock className="icon-s" aria-hidden="true" /> You lack the 'manage_settings' permission: the game's webhooks are visible to those who manage its settings.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const rows = hooksQ.data?.items ?? [];
  const open = deliveries.value ? (rows.find((h) => h.id === deliveries.value) ?? null) : null;
  return (
    <div data-testid="settings-webhooks" className="pane settings-block">
      <div className="pane-head">
        <h2>Webhooks</h2>
        {rows.length > 0 && <span className="muted">{rows.length}</span>}
        <div className="spacer" />
        <Button variant="primary" icon={<Plus aria-hidden="true" />} onClick={() => setCreating(true)}>
          Create…
        </Button>
      </div>
      <p className="settings-note">Every matching event of the game's stream is POSTed to the URL, signed with X-Huelency-Signature (HMAC-SHA256 of "&lt;t&gt;.&lt;body&gt;" with the secret); a failed delivery is retried after 1 m, 5 m, 30 m, 2 h and 12 h.</p>
      <div className="pane-body flush">
      {hooksQ.error ? (
        <div className="pane-body">
          <LoadError>The webhooks could not be read.</LoadError>
        </div>
      ) : hooksQ.loading && rows.length === 0 ? (
        <SkeletonRows />
      ) : rows.length === 0 ? (
        <EmptyState icon={<WebhookIcon />} title="No webhooks yet" body="Create one to notify a CI job, a chat or a backend of releases, pins, jobs or orders." />
      ) : (
        <table className="settings-table">
          <thead>
            <tr>
              <th scope="col">URL</th>
              <th scope="col">Events</th>
              <th scope="col">Enabled</th>
              <th scope="col">Description</th>
              <th scope="col">Secret</th>
              <th scope="col">Stream position</th>
              <th scope="col">Updated</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((h) => (
              <tr key={h.id} data-testid={`webhook-${h.id}`}>
                <td className="webhook-url">
                  <code>{h.url}</code>
                </td>
                <td>
                  <span className="settings-chips">
                    {h.events.map((e) => (
                      <Chip key={e} outline>
                        {e}
                      </Chip>
                    ))}
                  </span>
                </td>
                <td>
                  <Switch aria-label={`Enabled ${h.url}`} checked={h.enabled} disabled={busy === `${h.id}:enabled`} onChange={(v) => void setEnabled(h, v)} />
                  <span className="muted small"> {h.enabled ? 'on' : 'off'}</span>
                </td>
                <td>{h.description ?? <span className="muted">—</span>}</td>
                <td>
                  <code>
                    {h.secretPrefix}…{h.secretHint}
                  </code>
                </td>
                <td title="The seq of the last stream event the dispatcher considered for this hook">{String(h.lastEventSeq)}</td>
                <td>{when(h.updatedAt ?? h.createdAt)}</td>
                <td className="settings-actions">
                  <Button size="s" onClick={() => deliveries.open(h.id)}>
                    Deliveries…
                  </Button>
                  <Button size="s" onClick={() => void ping(h)} busy={busy === `${h.id}:ping`}>
                    Ping
                  </Button>
                  <Button size="s" onClick={() => setEditing(h)}>
                    Edit…
                  </Button>
                  <Button size="s" onClick={() => void rotate(h)} busy={busy === `${h.id}:rotate`}>
                    Rotate secret
                  </Button>
                  <Button size="s" variant="danger" onClick={() => void remove(h)} busy={busy === `${h.id}:delete`}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </div>
      <WebhookDialog
        open={creating}
        hook={null}
        onClose={() => setCreating(false)}
        onSubmit={async (body, idempotencyKey) => {
          const r: WebhookCreated = await client.data(ops.createWebhook, { path: { org, game: game.slug }, body, idempotencyKey });
          setCreating(false);
          setSecret({ title: 'Webhook created', secret: r.secret, hint: r.webhook.secretHint });
          reload();
        }}
      />
      <WebhookDialog
        open={editing !== null}
        hook={editing}
        onClose={() => setEditing(null)}
        onSubmit={async (body) => {
          if (!editing) return;
          await client.data(ops.updateWebhook, { path: hookPath(editing), body: { url: body.url, events: body.events, description: body.description }, ifMatch: Number(editing.version) });
          setEditing(null);
          toasts.success('Webhook saved.');
          reload();
        }}
      />
      <SecretOnce
        open={secret !== null}
        title={secret?.title}
        label="Signing secret"
        secret={secret?.secret ?? ''}
        hint={secret?.hint}
        note={<p className="small">Copy it now; it is never shown again. The receiver verifies X-Huelency-Signature with it.</p>}
        onClose={() => setSecret(null)}
      />
      <Modal title={open ? `Deliveries of ${open.url}` : 'Deliveries'} open={deliveries.value !== null} onClose={deliveries.close} drawer size="l">
        {deliveries.value !== null && (open ? <DeliveriesBody hook={open} when={when} /> : hooksQ.loading ? <SkeletonRows rows={3} label="Loading the webhook" /> : <EmptyState icon={<WebhookIcon />} title="No such webhook." body="It was deleted, or the link points at another game." />)}
      </Modal>
    </div>
  );
}

interface WebhookBody {
  url: string;
  events: string[];
  description: string;
  enabled?: boolean;
}

/** Create (hook null) or Edit: URL, the kind patterns as chips with hints, the description; Create also asks Enabled. */
function WebhookDialog({ open, hook, onClose, onSubmit }: { open: boolean; hook: Webhook | null; onClose: () => void; onSubmit: (body: WebhookBody, idempotencyKey: string) => Promise<void> }) {
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>([]);
  const [pattern, setPattern] = useState('');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<ReturnType<typeof asApiError> | null>(null);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (!open) return;
    setUrl(hook?.url ?? '');
    setEvents(hook ? [...hook.events] : []);
    setPattern('');
    setDescription(hook?.description ?? '');
    setEnabled(hook?.enabled ?? true);
    setError(null);
    setIdempotencyKey(crypto.randomUUID());
  }, [open, hook]);

  const add = (raw: string) => {
    const p = raw.trim();
    if (!p) return;
    setEvents((cur) => (cur.includes(p) ? cur : [...cur, p]));
    setPattern('');
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      add(pattern);
    } else if (e.key === 'Backspace' && pattern === '' && events.length > 0) {
      setEvents((cur) => cur.slice(0, -1));
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const all = pattern.trim() ? [...events.filter((x) => x !== pattern.trim()), pattern.trim()] : events;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ url: url.trim(), events: all, description: description.trim(), ...(hook ? {} : { enabled }) }, idempotencyKey);
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const testId = hook ? 'edit-webhook-dialog' : 'create-webhook-dialog';
  return (
    <Modal title={hook ? 'Edit webhook' : 'Create a webhook'} open={open} onClose={onClose} size="m">
      <form onSubmit={(e) => void submit(e)} data-testid={testId}>
        <Field label="URL" required help="https (http only for a loopback host)." error={error?.fieldMessage('url')}>
          <TextInput aria-label="URL" type="url" value={url} onChange={(e) => setUrl(e.target.value)} required autoComplete="off" placeholder="https://ci.example.com/huelency" />
        </Field>
        <Field label="Events" required help="Kind patterns: an exact kind (branch.head), a family (job.*), or * for everything. Enter adds one." error={error?.fieldMessage('events')}>
          <div className="webhook-events">
            <span className="settings-chips" role="list" aria-label="Event patterns">
              {events.map((p) => (
                <span role="listitem" key={p}>
                  <Chip outline onRemove={() => setEvents((cur) => cur.filter((x) => x !== p))} removeLabel={`Remove ${p}`}>
                    {p}
                  </Chip>
                </span>
              ))}
            </span>
            <TextInput aria-label="Event pattern" value={pattern} onChange={(e) => setPattern(e.target.value)} onKeyDown={onKey} autoComplete="off" placeholder="release_pin.create" />
          </div>
        </Field>
        <div className="webhook-hints" role="group" aria-label="Suggested patterns">
          {EVENT_HINTS.filter((h) => !events.includes(h)).map((h) => (
            <button key={h} type="button" className="chip chip-outline" onClick={() => add(h)} aria-label={`Add ${h}`}>
              + {h}
            </button>
          ))}
        </div>
        <Field label="Description" error={error?.fieldMessage('description')}>
          <TextArea aria-label="Description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
        </Field>
        {!hook && <Checkbox label="Enabled" aria-label="Enabled" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        {error && !error.details?.length && (
          <LoadError>{error.message}</LoadError>
        )}
        <div className="modal-footer form-footer">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" busy={busy} disabled={!url.trim() || (events.length === 0 && !pattern.trim())}>
            {hook ? 'Save' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** The deliveries of one hook, newest first, paged; the status filter; Redeliver on a failed or pending one. */
function DeliveriesBody({ hook, when }: { hook: Webhook; when: (v: string | null | undefined) => string }) {
  const { org, game } = useGame();
  const { client } = useAuth();
  const toasts = useToasts();
  const [status, setStatus] = useState<'' | 'pending' | 'delivered' | 'failed'>('');
  const [rows, setRows] = useState<WebhookDelivery[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [run, setRun] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const path = { org, game: game.slug, hook: hook.id };

  const load = async (after: string | null, append: boolean) => {
    setLoading(true);
    setError(null);
    try {
      const page = await client.data(ops.listWebhookDeliveries, { path, query: { status: status || undefined, limit: 50, cursor: after ?? undefined } });
      setRows((cur) => (append ? [...cur, ...page.items] : page.items));
      setCursor(page.nextCursor ?? null);
    } catch (e) {
      setError(asApiError(e).message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load(null, false);
  }, [status, run, hook.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const redeliver = async (d: WebhookDelivery) => {
    setBusy(d.id);
    try {
      const next = await client.data(ops.redeliverWebhookDelivery, { path: { ...path, delivery: d.id } });
      setRows((cur) => cur.map((x) => (x.id === next.id ? next : x)));
      toasts.success('Delivery queued again: the dispatcher sends it within seconds.');
    } catch (e) {
      const err = asApiError(e);
      toasts.error(`Redeliver failed: ${err.message}`, { requestId: err.requestId });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="webhook-deliveries">
      <div className="page-toolbar">
        <Field label="Status" inline>
          <Select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">all</option>
            <option value="pending">pending</option>
            <option value="delivered">delivered</option>
            <option value="failed">failed</option>
          </Select>
        </Field>
        <div className="spacer" />
        <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={() => setRun((n) => n + 1)} busy={loading}>
          Reload
        </Button>
      </div>
      {error ? (
        <LoadError>{error}</LoadError>
      ) : !loading && rows.length === 0 ? (
        <EmptyState icon={<Inbox />} title={`No deliveries${status ? ` with status ${status}` : ''} in the last 30 days.`} body="A delivery appears here as soon as a matching event is queued for this hook." />
      ) : (
        <div className="pane">
        <table className="settings-table">
          <thead>
            <tr>
              <th scope="col">Kind</th>
              <th scope="col">Status</th>
              <th scope="col">Attempts</th>
              <th scope="col">Next attempt</th>
              <th scope="col">Last answer</th>
              <th scope="col">Delivered</th>
              <th scope="col">
                <span className="visually-hidden">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} data-testid={`delivery-${d.id}`}>
                <td>
                  <code>{d.kind}</code>
                  <div className="muted small">seq {String(d.eventSeq)}</div>
                </td>
                <td>
                  <Badge tone={DELIVERY_TONE[d.status] ?? 'neutral'}>{d.status}</Badge>
                </td>
                <td>{String(d.attempts)}</td>
                <td>{d.status === 'pending' ? when(d.nextAttemptAt) : '—'}</td>
                <td className="webhook-answer">
                  {d.lastStatusCode !== null && d.lastStatusCode !== undefined && <code>HTTP {String(d.lastStatusCode)}</code>}
                  {d.lastError && <div className="error small webhook-error">{d.lastError}</div>}
                  {d.lastResponse && <pre className="webhook-response small">{d.lastResponse}</pre>}
                  {d.lastStatusCode == null && !d.lastError && !d.lastResponse && <span className="muted">—</span>}
                </td>
                <td>{when(d.deliveredAt)}</td>
                <td className="settings-actions">
                  {(d.status === 'failed' || d.status === 'pending') && (
                    <Button size="s" onClick={() => void redeliver(d)} busy={busy === d.id}>
                      Redeliver
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {cursor && (
        <div className="settings-more">
          <Button size="s" onClick={() => void load(cursor, true)} busy={loading} data-testid="deliveries-load-more">
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}
